/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Powerful features section showcasing 6 key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  imageUrl,
  imageAlt,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-lg text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Translate into 35 Languages Instantly',
      description:
        "FalcoCut enables you to localize your videos in over 35 languages and dialects with just a few clicks. Whether it's for YouTube, corporate training, or e-commerce product videos and ads, you can expand your audience without extra production costs. Forget about hiring translators or voice actors—our AI handles the heavy lifting while keeping your content authentic.",
      imageUrl: '/images/landing-translate-into-languages-instantly.png',
      imageAlt: 'Translate into 35 Languages Instantly',
    },
    {
      title: 'Extensive Library of Authentic AI Voices',
      description:
        'Bring your videos to life with over 97 natural AI voices across multiple languages and accents. Whether you need a confident presenter, a friendly teacher, or a casual influencer, FalcoCut gives you the right voice for every story. With advanced speech synthesis and lip-sync accuracy, your avatars sound as natural as real people, making global communication effortless.',
      imageUrl: '/images/landing-extensive-library-of-authentic-ai-voices.png',
      imageAlt: 'Extensive Library of Authentic AI Voices',
    },
    {
      title: 'AI Voice Cloning & Natural Dubbing',
      description:
        "Preserve the unique tone and personality of the original speaker with FalcoCut's advanced voice cloning. Instead of robotic audio, our AI generates natural-sounding voiceovers that reflect real human cadence and style. You can even choose from a diverse portfolio of AI voices if you prefer a different sound, making your content flexible for multiple markets.",
      imageUrl: '/images/landing-ai-voice-cloning-and-natural-dubbing.png',
      imageAlt: 'AI Voice Cloning & Natural Dubbing',
    },
    {
      title: 'Accurate Subtitles with Editing Options',
      description:
        "FalcoCut automatically transcribes and translates subtitles with up to 99.9% accuracy. Customize font, color, size, and placement to match your brand identity. It's ideal for e-commerce brands localizing product demos or ad creatives across global marketplaces like Amazon or Shopify. High-quality subtitles make your content more engaging, SEO-friendly, and accessible for viewers watching without sound.",
      imageUrl: '/images/landing-accurate-subtitles-with-editing-options.png',
      imageAlt: 'Accurate Subtitles with Editing Options',
    },
    {
      title: 'Seamless Lip Sync for Realism',
      description:
        "Unlike basic video translators, FalcoCut ensures that translated voices stay perfectly in sync with lip movements. The result is a smooth, immersive viewing experience that feels native to your target audience. Whether you're dubbing interviews, presentations, or promotional videos, lip sync eliminates the awkwardness and makes your translation look truly professional.",
      imageUrl: '/images/landing-seamless-lip-sync-for-realism.png',
      imageAlt: 'Seamless Lip Sync for Realism',
    },
    {
      title: 'All-in-One Video Localization Platform',
      description:
        "From transcription and translation to dubbing, subtitling, and exporting, FalcoCut combines every essential localization tool into one intuitive platform. You don't need multiple apps or expensive post-production. With FalcoCut, you can translate, edit, and publish videos in minutes—ready to reach global audiences across YouTube, TikTok, Instagram, or internal training systems.",
      imageUrl: '/images/landing-all-in-one-video-localization-platform.png',
      imageAlt: 'All-in-One Video Localization Platform',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Powerful AI Video Translation
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          FalcoCut brings everything you need to localize videos into one platform. From
          AI voice cloning and seamless lip sync to accurate subtitles and multi-language
          dubbing, our tools make it simple to create authentic, global-ready content in
          minutes.
        </p>
        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
